import { useRef, useEffect, useState, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// Models
import IInsuranceCase from 'app/models/Case';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { createConversation, createConversationFromPages, createQuestion } from 'app/store/AIChat/AIChat.async';
// Actions
import { AIChatActions } from 'app/store/AIChat/AIChat.slice';
// Selectors
import { selectPreviewPageOpen } from 'app/store/AppUI/AppUI.selectors';
import { selectInsuranceCase } from 'app/store/Cases/Cases.selectors';
import { selectConversation, selectSelectedPromptContent } from 'app/store/AIChat/AIChat.selectors';
import { selectAIChatEligibleDocumentsAsOptions, selectHasAnyDocumentOcred } from 'app/store/DMSDocuments/DMSDocuments.selectors';
// Mui
import { SxProps, Box, Paper, Tooltip, InputBase, FormHelperText } from '@mui/material';
// Icons
import { Send as SendIcon } from '@mui/icons-material';
// Components
import { IconButton } from 'app/components/Mui';
// 
import ChatMessagePromptButton from './ChatMessagePromptButton';
import ChatMessageDocumentButton from './ChatMessageDocumentButton';
import ChatMessageCoverageButton from './ChatMessageCoverageButton';
import { isMaxLength } from 'app/utilities/Validations';

interface IFormData {
  question: {
    text?: string;
    promptId?: number;
    coverage?: number | null;
  };
  documentIds?: number[];
}

type Props = {
  sx?: SxProps;
  pages?: any[];
  source?: 'files' | 'workspace' | 'search';
  cb?: () => void;
}

export default function ChatMessageForm({ sx = {
  position: 'sticky',
  bottom: 0,
  bgcolor: '#f7f7f7',
  pb: 4,
  px: 8
}, pages, source, cb }:Props) {
  const { t } = useTranslation();

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const insuranceCase = useAppSelector(selectInsuranceCase) as IInsuranceCase;
  const conversation = useAppSelector(selectConversation);
  const documentOptions = useAppSelector(selectAIChatEligibleDocumentsAsOptions);
  const hasAnyDocumentOcred = useAppSelector(selectHasAnyDocumentOcred);
  const previewPageOpen = useAppSelector(selectPreviewPageOpen);
  const selectedPromptContent = useAppSelector(selectSelectedPromptContent);

  const [loading, setLoading] = useState(false);

  const documentIds = documentOptions?.map(({ value }) => value) || [];

  const formRef = useRef<HTMLFormElement | null>(null);

  const methods = useForm<IFormData>({
    defaultValues: {
      question: {
        text: '',
        promptId: undefined,
        coverage: null
      },
      documentIds: []
    },
  });
  const { handleSubmit, register, reset, watch, setValue, formState: { isSubmitted } } = methods;

  const hasPages = useMemo(() => {
    if ( pages && pages.length > 0 ) return true;
    return conversation && conversation.pageIds && conversation.pageIds.length > 0;
  }, [pages, conversation]);

  const annotationSourceHelperText = useMemo(() => {
    if (!conversation || !conversation.annotations) return '';
    const sourceAnnotation = conversation.annotations.find(annotation => annotation.name === 'source');
    if (!sourceAnnotation) return '';
    if (sourceAnnotation.value === 'files') return t('pages.aiChat.filesSourceHelperText');
    if (sourceAnnotation.value === 'search') return t('pages.aiChat.searchSourceHelperText');
    if (sourceAnnotation.value === 'workspace') return t('pages.aiChat.workspaceSourceHelperText');
    return '';
  }, [conversation, t]);

  const onSubmit = handleSubmit((data) => {
    const { question, documentIds } = data;

    const questionData = Object.fromEntries(
      Object.entries(question).filter(([_, v]) => v !== undefined && v !== null && v !== '')
    );

    if ( conversation ){
      dispatch(createQuestion({
        conversationId: conversation.id,
        data: questionData
      }));
    } else {
      const initialChatData = {
        caseId: insuranceCase.id,
        name: question.text && question.text.length > 50
          ? question.text.slice(0, 47) + '...'
          : question.text
        ,
        settings: {
          ragMaxPages: 40
        },
        private: true
      }
      const chatData:any = { ...initialChatData, question: questionData };
      if ( documentIds?.length ) chatData.documentIds = documentIds;
      if ( hasPages && pages ) {
        chatData.pageIds = pages.map(page => ({
          documentId: page.documentId,
          page: page.pageNum
        }));
      }
      if ( source ) chatData.annotations = [{ name: 'source', value: source }];
      if ( hasPages ){
        asyncCreateConversationFromPages(chatData);
      } else {
        asyncCreateConversation(chatData);
      }
    }
  });

  const asyncCreateConversation = async (data:any) => {
    setLoading(true);
    try {
      await dispatch(createConversation(data)).unwrap();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const asyncCreateConversationFromPages = async (data:any) => {
    setLoading(true);
    try {
      await dispatch(createConversationFromPages(data)).unwrap();
      if ( cb ) cb();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if ( !conversation || conversation.status === 'idle' ){
      reset({
        question: {
          text: '',
          promptId: undefined,
          coverage: null
        },
        documentIds: hasPages ? [] : (conversation?.documentIds || documentIds || [])
      });
    }
    // eslint-disable-next-line
  }, [conversation, hasPages]);

  useEffect(() => {
    if ( !selectedPromptContent ) return;
    setValue('question.text', selectedPromptContent);
    setTimeout(() => {
      dispatch(AIChatActions.setSelectedPromptContent(''));
    }, 0);
    // eslint-disable-next-line
  }, [selectedPromptContent]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
     // Hz how it works, but when I remove this line, "Shift+Enter" stop works
    if ( event.key === 'Enter' && event.shiftKey ){
      // event.preventDefault(); // Prevent default behavior (new line)
    } else if ( event.key === 'Enter' ){
      event.preventDefault(); // Prevent default behavior (form submission)
      formRef.current?.requestSubmit();
    }
  };

  const watchText = watch('question.text');
  const textLength = watchText?.length || 0;
  const isLoading = conversation?.status === 'answering';
  const hasProcessedDocumentsAndCaseReady = hasAnyDocumentOcred && insuranceCase?.processing?.status === 'ready';
  const disableControls = !Boolean(hasProcessedDocumentsAndCaseReady) || isLoading || loading;
  const disableSendButton = disableControls || watchText === '' || loading;
  const isTextLengthInvalid = isSubmitted && textLength > 1000;

  return (
    <FormProvider {...methods}>
      <Box
        sx={sx}
        ref={formRef}
        component="form"
        onSubmit={onSubmit}
        noValidate
        autoComplete="off"
      >
        <input {...register('question.promptId')} type="hidden" />
        <input {...register('question.coverage')} type="hidden" />
        <input {...register('documentIds')} type="hidden" />
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            width: '100%',
            maxWidth: 856,
            borderRadius: 2,
            mr: previewPageOpen ? 'initial' : 'auto',
            ml: 'auto',
            p: 2
          }}
        >
          <InputBase
            {...register('question.text', {
              maxLength: isMaxLength(1000)
            })}
            sx={{
              p: 2
            }}
            placeholder={t('labels.message')}
            fullWidth
            multiline
            maxRows={4}
            margin="none"
            size="small"
            disabled={disableControls}
            onKeyDown={handleKeyDown}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChatMessagePromptButton disableControls={disableControls} />
              {!hasPages && <ChatMessageDocumentButton disableControls={disableControls} />}
              <ChatMessageCoverageButton disableControls={disableControls} />
            </Box>
            {annotationSourceHelperText ? (
              <FormHelperText sx={{
                flexGrow: 1,
                textAlign: 'center',
                color: (theme) => theme.palette.primary.main
              }}>{annotationSourceHelperText}</FormHelperText> 
            ) : null}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                minWidth: annotationSourceHelperText
                  ? 146
                  : 'auto',
                textAlign: 'right'
              }}
            >
              <FormHelperText sx={{
                display: 'flex',
                gap: 2,
                mr: 1,
                color: (theme) => isTextLengthInvalid
                  ? theme.palette.error.main 
                  : theme.palette.text.secondary
              }}>
                {isTextLengthInvalid ? <span>{t('validations.maxCharactersSize', { value: 1000 })}</span> : ''}
                {textLength} / 1000
              </FormHelperText>
              <Tooltip title={t('buttons.sendMessage')}>
                {/* Tooltip doesn`t work with disabled button, span added */}
                <span>
                  <IconButton
                    name="Send message"
                    loading={isLoading || loading}
                    type="submit"
                    disabled={disableSendButton}
                  ><SendIcon /></IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
      </Box>
    </FormProvider>
  )
}
